import { inject, Pipe, PipeTransform } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import dayjs from 'dayjs';

interface Translations {
  today?: string;
  yesterday?: string;
  at?: string;
  on?: string;
}

/**
 * It returns date displayable for user feed
 * Example:
 * Current time is 2022-30-03 10:43 ==> '2022-30-03 10:23' | feedDate = '10h23'
 * Current time is 2022-30-04 10:43 ==> '2022-30-03 19:23' | feedDate = 'Yesterday at 19h23'
 * Current time is 2022-30-05 10:43 ==> '2022-30-03 10:22' | feedDate = '2022/30/03 at 10:22'
 */
@Pipe({
  name: 'feedDate',
  standalone: true,
})
export class FeedDatePipe implements PipeTransform {
  private translations: Translations = {};
  private currentTranslationLang;

  constructor(private readonly translate: TranslateService) {}

  transform(date: Date, currentLang: string, format?: string): unknown {
    let hourFormat: string = format?.split(' ')[2] ? 'hh:mm A' : 'HH:mm';

    if (format) {
      format = format.split(' ')[0].replace('Y', 'YYYY').replace('d', 'DD');
    } else format = 'DD/MM/YYYY';

    if (currentLang && currentLang !== this.currentTranslationLang) {
      this.updateTranslations();
      this.currentTranslationLang = currentLang;
    } else if (!this.currentTranslationLang) {
      this.currentTranslationLang = this.translate.currentLang;
      this.updateTranslations();
    }
    const instant: Date = new Date();
    const dayJsDate: dayjs.Dayjs = dayjs(date);
    if (this.isSameDay(dayJsDate, instant)) {
      return dayJsDate.format(
        `[${this.translations.today} ${this.translations.at}] ${hourFormat}`
      );
    }
    if (this.isYesterday(dayJsDate, instant)) {
      return dayJsDate.format(
        `[${this.translations.yesterday} ${this.translations.at}] ${hourFormat}`
      );
    }
    if (this.isSameYear(dayJsDate, instant)) {
      return dayJsDate.format(
        `[${this.translations.on}] ${format.slice(0, 5)}  [${this.translations.at}] ${hourFormat}`
      );
    }
    return dayJsDate.format(
      `[${this.translations.on}] ${format} [${this.translations.at}] ${hourFormat}`
    );
  }

  private isSameDay(date: dayjs.Dayjs, currentDate: Date): boolean {
    return date.isSame(currentDate, 'day');
  }

  private isYesterday(date: dayjs.Dayjs, currentDate: Date): boolean {
    const yesterday = dayjs(currentDate).subtract(1, 'day');
    return date.isSame(yesterday, 'day');
  }

  private isSameYear(date: dayjs.Dayjs, currentDate: Date) {
    return date.year() === currentDate.getFullYear();
  }

  private updateTranslations() {
    this.translations = {
      today: this.translate.instant('date.today'),
      yesterday: this.translate.instant('date.yesterday'),
      at: this.translate.instant('date.at'),
      on: this.translate.instant('date.on'),
    };
  }
}
